
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {AccountState} from '../core/AccountState';
import {FieldModelAccountState} from '../core/AccountState';

/**
 * AccountStatesUpdateNotify struct
 */
class AccountStatesUpdateNotify {
  /**
   * Initialize struct
   * @param {!Map=} accountStates
   * @constructor
   */
  constructor (accountStates = new Map()) {
    this.AccountStates = accountStates
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountStatesUpdateNotify}
   * @param {!AccountStatesUpdateNotify} other Other struct
   * @returns {!AccountStatesUpdateNotify} This struct
   */
  copy (other) {
    if (other.AccountStates != null) {
      this.AccountStates = new Map()
      Object.keys(other.AccountStates).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = UInt64.fromNumber(key)
          if (other.AccountStates[key] != null) {
            let tempValue
            tempValue = AccountState.fromObject(other.AccountStates[key])
            this.AccountStates.set(tempKey, tempValue)
          } else {
            this.AccountStates.set(tempKey, null)
          }
        }
      })
    } else {
      this.AccountStates = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountStatesUpdateNotify}
   * @returns {!AccountStatesUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountStatesUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountStatesUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountStatesUpdateNotify}
   * @param {!AccountStatesUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountStatesUpdateNotify)) {
      throw new TypeError('Instance of AccountStatesUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountStatesUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      AccountStates: ((this.AccountStates != null) ? fbe.MapToObject(new Map(Array.from(this.AccountStates, item => [((item[0] != null) ? item[0].toNumber() : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountStatesUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountStatesUpdateNotify} other Object value
   * @returns {!AccountStatesUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new AccountStatesUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountStatesUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountStatesUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountStatesUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6367
  }
}

export { AccountStatesUpdateNotify };

/**
 * Fast Binary Encoding AccountStatesUpdateNotify field model
 */
class FieldModelAccountStatesUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.AccountStates = new fbe.FieldModelMap(new fbe.FieldModelUInt64(buffer, 4 + 4), new FieldModelAccountState(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.AccountStates.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.AccountStates.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountStatesUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6367
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountStates.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountStates.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountStates.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!AccountStatesUpdateNotify} fbeValue Default value, defaults is new AccountStatesUpdateNotify()
   * @returns {!AccountStatesUpdateNotify} AccountStatesUpdateNotify value
   */
  get (fbeValue = new AccountStatesUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!AccountStatesUpdateNotify} fbeValue AccountStatesUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountStates.fbeSize) <= fbeStructSize) {
      this.AccountStates.get(fbeValue.AccountStates)
    } else {
      fbeValue.AccountStates.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountStates.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {!AccountStatesUpdateNotify} fbeValue AccountStatesUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountStatesUpdateNotify}
   * @param {AccountStatesUpdateNotify} fbeValue AccountStatesUpdateNotify value
   */
  setFields (fbeValue) {
    this.AccountStates.set(fbeValue.AccountStates)
  }
}

export { FieldModelAccountStatesUpdateNotify };

/**
 * Fast Binary Encoding AccountStatesUpdateNotify model
 */
class AccountStatesUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountStatesUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the AccountStatesUpdateNotify model
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!FieldModelAccountStatesUpdateNotify} model AccountStatesUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountStatesUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountStatesUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountStatesUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountStatesUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountStatesUpdateNotifyModel}
   * @param {!AccountStatesUpdateNotify} value AccountStatesUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountStatesUpdateNotifyModel}
   * @param {!AccountStatesUpdateNotify} value AccountStatesUpdateNotify value, defaults is new AccountStatesUpdateNotify()
   * @return {!object} Deserialized AccountStatesUpdateNotify value and its size
   */
  deserialize (value = new AccountStatesUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountStatesUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountStatesUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountStatesUpdateNotifyModel}
   * @param {!number} prev Previous AccountStatesUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountStatesUpdateNotifyModel };
